import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import {
  CalendarOutlined,
  CloseOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  Button, DatePicker, Input, Modal, Radio, Select,
} from "antd";
// eslint-disable-next-line import/no-extraneous-dependencies
import Lottie from "lottie-react";
import moment from "moment";
import { toast } from "react-toastify";

import { formatSubmissionTestsForHeader } from "./submissionExpiryUtils";

import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../../../Common/UIComponents/SelectPopupScrollbar";
import { convertUtcToLocal } from "../../../Common/utils/dateUtils";

import {
  addSubmissionTrackingInfo,
  resolveExpiredSubmission,
} from "../../../../actions/sampleSubmission";

import SuccessAnimationData from "../../../../assets/images/common/animation/SuccessAnimation.json";
import "./SubmissionShippedFlowModal.css";
import "../../SampleSubmissionModal/SampleSubmissionConfirmModal.css";
import { ReactComponent as ExpiryInfoCircleOutlined } from "../../../../assets/images/sample-submission/ExpiryInfoCircleOutlined.svg";
import { ReactComponent as LightBulb } from "../../../../assets/images/sample-submission/LightBulb.svg";
import { ReactComponent as PackageOutlined } from "../../../../assets/images/sample-submission/PackageOutlined.svg";
import { ReactComponent as RectangleLine } from "../../../../assets/images/sample-submission/RectangleLine.svg";

const COURIER_OPTIONS = [
  { label: "FedEx Corp", value: "FedEx" },
  { label: "Other Courier Services", value: "Others" },
];

const SHIPPING_CHOICE = {
  YES: "yes",
  NO: "no",
};

const FLOW_STEP = {
  CHOICE: "choice",
  SHIPMENT_FORM: "shipment_form",
  SCHEDULE: "schedule",
  SUCCESS: "success",
};

const SUCCESS_MESSAGES = {
  shipped: {
    title: "Shipment details saved",
    body: "Thank you! The lab will be notified that the sample is in transit.",
  },
  submitted: {
    title: "Submission submitted",
    body: "Your sample shipment is submitted for today",
  },
  scheduled: {
    title: "Submission scheduled",
    body: "Your sample shipment is scheduled for",
  },
};

const getScheduleSuccessBody = (dateKey) => (
  `${SUCCESS_MESSAGES.scheduled.body} ${moment(dateKey).format("MMM D, YYYY")}`
);

function ChoiceAlert({ shippingChoice }) {
  if (shippingChoice === SHIPPING_CHOICE.YES) {
    return (
      <div className="ShippedFlowModal__Alert ShippedFlowModal__Alert--info">
        <ExpiryInfoCircleOutlined aria-hidden />
        <div>
          <p className="ShippedFlowModal__AlertTitle">Provide shipment details</p>
          <p className="ShippedFlowModal__AlertText">
            Share the shipment tracking number so we can monitor delivery and confirm when the sample reaches the lab.
          </p>
        </div>
      </div>
    );
  }

  if (shippingChoice === SHIPPING_CHOICE.NO) {
    return (
      <div className="ShippedFlowModal__Alert ShippedFlowModal__Alert--warning">
        <ExpiryInfoCircleOutlined aria-hidden />
        <div>
          <p className="ShippedFlowModal__AlertTitle">Why reschedule?</p>
          <p className="ShippedFlowModal__AlertText">
            The lab has not received your sample yet. To keep testing timelines accurate, select the date you plan to ship the sample.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ShippedFlowModal__Alert ShippedFlowModal__Alert--warning">
      <LightBulb aria-hidden />
      <div>
        <p className="ShippedFlowModal__AlertTitle">Required Action</p>
        <p className="ShippedFlowModal__AlertText">
          If no action is taken within the day(s) mentioned, this submission will be moved back to Draft automatically.
        </p>
        {/* <button type="button" className="ShippedFlowModal__LearnMore">
          Learn more
          <Arrow aria-hidden />
        </button> */}
      </div>
    </div>
  );
}

export default function SubmissionShippedFlowModal({
  open,
  submission,
  onClose,
  onSuccess,
}) {
  const [step, setStep] = useState(FLOW_STEP.CHOICE);
  const [shippingChoice, setShippingChoice] = useState("");
  const [courier, setCourier] = useState(undefined);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [shipmentDate, setShipmentDate] = useState(null);
  const [shipmentComments, setShipmentComments] = useState("");
  const [scheduleComment, setScheduleComment] = useState("");
  const [selectedScheduleDateKey, setSelectedScheduleDateKey] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successType, setSuccessType] = useState("shipped");
  const lottieRef = useRef(null);

  const playSuccessAnimation = () => {
    if (lottieRef.current) {
      lottieRef.current.goToAndPlay(0, true);
    }
  };

  useEffect(() => {
    if (step !== FLOW_STEP.SUCCESS) return undefined;
    const timer = window.setTimeout(playSuccessAnimation, 200);
    return () => window.clearTimeout(timer);
  }, [step, successType]);

  const scheduleDateOptions = useMemo(() => (
    Array.from({ length: 5 }, (_, index) => {
      const date = moment().add(index, "days");
      return {
        key: date.format("YYYY-MM-DD"),
        month: date.format("MMM"),
        day: date.format("D"),
        isToday: index === 0,
      };
    })
  ), []);

  const submittedOnText = submission?.submit_date
    ? moment(new Date(convertUtcToLocal(submission.submit_date))).format("MMM D, YYYY")
    : "-";
  const testsText = formatSubmissionTestsForHeader(submission);

  const resetForm = () => {
    setStep(FLOW_STEP.CHOICE);
    setShippingChoice("");
    setCourier(undefined);
    setTrackingNumber("");
    setShipmentDate(null);
    setShipmentComments("");
    setScheduleComment("");
    setSelectedScheduleDateKey("");
    setSubmitting(false);
    setSuccessType("shipped");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isShipmentFormValid = useMemo(() => {
    if (!courier || !shipmentDate) return false;
    if (courier === "FedEx" && !trackingNumber.trim()) return false;
    return true;
  }, [courier, shipmentDate, trackingNumber]);

  const handleChoiceNext = () => {
    if (shippingChoice === SHIPPING_CHOICE.YES) {
      setStep(FLOW_STEP.SHIPMENT_FORM);
      return;
    }
    if (shippingChoice === SHIPPING_CHOICE.NO) {
      setStep(FLOW_STEP.SCHEDULE);
    }
  };

  const handleConfirmShipment = async () => {
    if (!isShipmentFormValid || !submission) return;

    setSubmitting(true);
    const { success, message } = await addSubmissionTrackingInfo({
      submission_id: submission.submit_id,
      courier,
      select_date: shipmentDate.format("YYYY-MM-DD"),
      comments: shipmentComments.trim(),
      tracking_numbers: courier === "FedEx" ? trackingNumber.trim() : "",
    });
    setSubmitting(false);

    if (success) {
      setSuccessType("shipped");
      setStep(FLOW_STEP.SUCCESS);
      if (typeof onSuccess === "function") onSuccess();
      return;
    }
    toast.error(message);
  };

  const handleScheduleConfirm = async () => {
    if (!selectedScheduleDateKey || !submission) return;

    setSubmitting(true);
    const isToday = moment(selectedScheduleDateKey).isSame(moment(), "day");
    const payload = {
      submission_id: submission.submit_id,
      action: isToday ? "resubmit" : "reschedule",
    };
    if (!isToday) {
      payload.schedule_date = selectedScheduleDateKey;
    }

    const { success, message } = await resolveExpiredSubmission(payload);
    setSubmitting(false);

    if (success) {
      setSuccessType(isToday ? "submitted" : "scheduled");
      setStep(FLOW_STEP.SUCCESS);
      if (typeof onSuccess === "function") onSuccess();
      return;
    }
    toast.error(message);
  };

  const renderChoiceStep = () => (
    <>
      <ChoiceAlert shippingChoice={shippingChoice} submission={submission} />
      <p className="ShippedFlowModal__Question">Have you already shipped the sample?</p>
      <Radio.Group
        className="ShippedFlowModal__Options"
        name="shipped-flow-choice"
        value={shippingChoice}
        onChange={(e) => setShippingChoice(e.target.value)}
      >
        <div
          className={`ShippedFlowModal__Option${shippingChoice === SHIPPING_CHOICE.YES ? " ShippedFlowModal__Option--active" : ""}`}
          onClick={() => setShippingChoice(SHIPPING_CHOICE.YES)}
        >
          <Radio value={SHIPPING_CHOICE.YES} />
          <PackageOutlined className="ShippedFlowModal__OptionIcon ShippedFlowModal__OptionIcon--shipped" aria-hidden />
          <div className="ShippedFlowModal__OptionText">
            <span className="ShippedFlowModal__OptionTitle">Yes, I have shipped it.</span>
            <span className="ShippedFlowModal__OptionDesc">Provide shipment details so we can track it.</span>
          </div>
          <RightOutlined className="ShippedFlowModal__OptionChevron" aria-hidden />
        </div>
        <div
          className={`ShippedFlowModal__Option${shippingChoice === SHIPPING_CHOICE.NO ? " ShippedFlowModal__Option--active" : ""}`}
          onClick={() => setShippingChoice(SHIPPING_CHOICE.NO)}
        >
          <Radio value={SHIPPING_CHOICE.NO} />
          <CalendarOutlined className="ShippedFlowModal__OptionIcon ShippedFlowModal__OptionIcon--calendar" aria-hidden />
          <div className="ShippedFlowModal__OptionText">
            <span className="ShippedFlowModal__OptionTitle">No, I have not shipped it yet.</span>
            <span className="ShippedFlowModal__OptionDesc">Reschedule shipment to keep testing on track.</span>
          </div>
          <RightOutlined className="ShippedFlowModal__OptionChevron" aria-hidden />
        </div>
      </Radio.Group>
      <div className="SampleSubmissionConfirmModal__Divider" />
      <div className="ShippedFlowModal__Footer">
        <Button type="default" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="primary"
          disabled={!shippingChoice}
          onClick={handleChoiceNext}
        >
          Next
        </Button>
      </div>
    </>
  );

  const renderShipmentFormStep = () => (
    <>
      <div className="ShippedFlowModal__SectionHeader">
        <h4>Provide Shipment Details</h4>
        <p>Share tracking information so the lab can monitor the shipment.</p>
      </div>
      <div className="ShippedFlowModal__Form">
        <label className="ShippedFlowModal__Field">
          <span>Courier *</span>
          <Select
            value={courier}
            placeholder="Select courier service"
            options={COURIER_OPTIONS}
            onChange={(value) => {
              setCourier(value);
              if (value !== "FedEx") setTrackingNumber("");
            }}
            className="ShippedFlowModal__Select"
            {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
            classNames={selectPopupClassNames()}
            virtual={false}
            listHeight={256}
            popupRender={(menu) => renderSelectPopupScrollbar(menu, {
              optionCount: COURIER_OPTIONS.length,
            })}
          />
        </label>
        {courier === "FedEx" && (
          <label className="ShippedFlowModal__Field">
            <span>Tracking Number *</span>
            <Input
              value={trackingNumber}
              placeholder="Enter tracking number"
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="ShippedFlowModal__Input"
            />
          </label>
        )}
        <label className="ShippedFlowModal__Field">
          <span>Select Date *</span>
          <DatePicker
            value={shipmentDate}
            onChange={setShipmentDate}
            format="DD-MM-YYYY"
            placeholder="Select date"
            className="ShippedFlowModal__DatePicker"
          />
        </label>
        <label className="ShippedFlowModal__Field">
          <span>Comments (Optional)</span>
          <Input.TextArea
            value={shipmentComments}
            placeholder="Add any additional information"
            rows={3}
            className="ShippedFlowModal__Textarea"
            onChange={(e) => setShipmentComments(e.target.value)}
          />
        </label>
      </div>
      <div className="SampleSubmissionConfirmModal__Divider" />
      <div className="ShippedFlowModal__Footer">
        <Button type="default" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="primary"
          loading={submitting}
          disabled={!isShipmentFormValid}
          onClick={handleConfirmShipment}
        >
          Confirm Shipment
        </Button>
      </div>
    </>
  );

  const renderScheduleStep = () => (
    <>
      {/* <div className="ShippedFlowModal__SectionHeader">
        <h4>Schedule Submission</h4>
        <p>We&apos;ll remind you before the scheduled date.</p>
      </div> */}
      <div className="ShippedFlowModal__ScheduleComment">
        <p className="ShippedFlowModal__ScheduleCommentLabel">Add Comment</p>
        <Input.TextArea
          value={scheduleComment}
          placeholder="Enter a description..."
          rows={4}
          className="ShippedFlowModal__ScheduleTextarea ShippedFlowModal__Textarea"
          onChange={(e) => setScheduleComment(e.target.value)}
        />
      </div>
      <div className="sampleSubmissionSchedule">
        <div className="sampleSubmissionSchedule__Title">When would you like submit this submission?</div>
        <div className="sampleSubmissionSchedule__Row">
          <div className="sampleSubmissionSchedule__Dates">
            {scheduleDateOptions.map((item) => (
              <div key={item.key} className="sampleSubmissionSchedule__DateItem">
                <button
                  type="button"
                  className={`sampleSubmissionSchedule__DatePill ${selectedScheduleDateKey === item.key ? "sampleSubmissionSchedule__DatePill--active" : ""}`}
                  onClick={() => setSelectedScheduleDateKey(item.key)}
                >
                  <span>{item.month}</span>
                  <span>{item.day}</span>
                </button>
                {item.isToday && (
                  <span className="sampleSubmissionSchedule__TodayLabel">Today</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="SampleSubmissionConfirmModal__Divider" />
      <div className="ShippedFlowModal__Footer">
        <Button type="default" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="primary"
          loading={submitting}
          disabled={!selectedScheduleDateKey}
          onClick={handleScheduleConfirm}
        >
          Submit
        </Button>
      </div>
    </>
  );

  const renderSuccessStep = () => {
    const message = SUCCESS_MESSAGES[successType] || SUCCESS_MESSAGES.shipped;
    const title = message.title;
    const body = successType === "scheduled" && selectedScheduleDateKey
      ? getScheduleSuccessBody(selectedScheduleDateKey)
      : message.body;
    return (
      <div className="ShippedFlowModal__Success">
        <div className="ShippedFlowModal__SuccessAnimation" aria-hidden>
          <Lottie
            lottieRef={lottieRef}
            animationData={SuccessAnimationData}
            loop={false}
            autoplay={false}
          />
        </div>
        <h4>{title}</h4>
        <p>{body}</p>
      </div>
    );
  };

  if (!submission) return null;

  return (
    <Modal
      title=""
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      destroyOnClose
      width={step === FLOW_STEP.SUCCESS ? 420 : 560}
      wrapClassName={`ShippedFlowModal ${step === FLOW_STEP.SUCCESS ? "ShippedFlowModal--success" : ""}`}
      closeIcon={<CloseOutlined />}
    >
      {step !== FLOW_STEP.SUCCESS && (
        <div className="ShippedFlowModal__Header">
          <RectangleLine aria-hidden />
          <div>
            <div className="ShippedFlowModal__HeaderTitle">{submission.submit_name}</div>
            <div className="ShippedFlowModal__HeaderMeta">
              {`PO Number: ${submission.po || "-"}, Submitted on: ${submittedOnText}, Status: Submitted, Tests: ${testsText}`}
            </div>
          </div>
        </div>
      )}

      <div className="ShippedFlowModal__Body">
        {step === FLOW_STEP.CHOICE && renderChoiceStep()}
        {step === FLOW_STEP.SHIPMENT_FORM && renderShipmentFormStep()}
        {step === FLOW_STEP.SCHEDULE && renderScheduleStep()}
        {step === FLOW_STEP.SUCCESS && renderSuccessStep()}
      </div>
    </Modal>
  );
}
